import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import {
  applyFloorImageLayer,
  branchHasMultipleFloors,
  DEFAULT_FLOOR_ALL,
  getBranchFloorOptions,
  getStoreMapCenter,
  MAPBOX_DEFAULT_BEARING,
  MAPBOX_STYLE,
  parseMapCoordinates,
  setMapboxAccessToken,
} from "../../../functions/branchMapboxUtils";

const FOCUSED_MARKER_COLOR = "#2563eb";
const DEFAULT_MARKER_COLOR = "#64748b";

const MdmLocationVersionMapPanel = ({
  deviceEntries = [],
  focusedDeviceId,
  locationSource = "logs",
  retailerId,
  branchId,
  storeCoordinates,
  branchMapData,
  isBranchMapDataLoading,
  branchMapDataError,
  isMapActive,
  fillHeight = false,
  emptyCoordinatesMessage = "",
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [floor, setFloor] = useState(DEFAULT_FLOOR_ALL);
  const [mapReady, setMapReady] = useState(false);

  const storeCenter = useMemo(
    () => getStoreMapCenter(storeCoordinates),
    [storeCoordinates]
  );
  const floorOptions = getBranchFloorOptions(branchMapData);
  const showFloorPicker = branchHasMultipleFloors(branchMapData);

  const plottedDevices = useMemo(
    () =>
      deviceEntries
        .map((entry) => {
          const coordinates = parseMapCoordinates({
            locationLatitude: entry.latitude,
            locationLongitude: entry.longitude,
          });
          if (!coordinates) return null;
          return { deviceId: entry.deviceId, coordinates };
        })
        .filter(Boolean),
    [deviceEntries]
  );

  const hasRetailerBranchIds =
    retailerId !== null &&
    retailerId !== undefined &&
    branchId !== null &&
    branchId !== undefined;
  const canRenderMap = Boolean(hasRetailerBranchIds && storeCoordinates && isMapActive);

  const getMapStatusMessage = () => {
    if (isBranchMapDataLoading) return "Loading branch map...";
    if (branchMapDataError) return branchMapDataError;
    if (!hasRetailerBranchIds) {
      return "Cannot load branch map: missing retailer or branch ID for this branch.";
    }
    if (!storeCoordinates) {
      return "No branch map configuration found for this store.";
    }
    return "";
  };

  const mapStatusMessage = getMapStatusMessage();

  const clearMarkers = () => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
  };

  useEffect(() => {
    setFloor(DEFAULT_FLOOR_ALL);
  }, [retailerId, branchId]);

  useEffect(() => {
    if (!canRenderMap || !mapContainerRef.current || !storeCenter) return undefined;

    setMapboxAccessToken();
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: [storeCenter.lng, storeCenter.lat],
      zoom: 18,
      bearing: MAPBOX_DEFAULT_BEARING,
    });
    mapRef.current = map;
    setMapReady(false);

    const handleLoad = () => {
      setMapReady(true);
      map.resize();
    };
    map.on("load", handleLoad);

    return () => {
      map.off("load", handleLoad);
      clearMarkers();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [canRenderMap, retailerId, branchId, storeCoordinates, storeCenter]);

  useEffect(() => {
    if (!mapRef.current || !mapReady || !storeCoordinates) return;
    applyFloorImageLayer(mapRef.current, storeCoordinates, floor);
  }, [floor, mapReady, storeCoordinates]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    clearMarkers();

    plottedDevices.forEach(({ deviceId, coordinates }) => {
      const isFocused = focusedDeviceId === deviceId;
      const marker = new mapboxgl.Marker({
        color: isFocused ? FOCUSED_MARKER_COLOR : DEFAULT_MARKER_COLOR,
        scale: isFocused ? 0.9 : 0.75,
      })
        .setLngLat([coordinates.lng, coordinates.lat])
        .addTo(map);
      markersRef.current.push(marker);
    });
  }, [focusedDeviceId, mapReady, plottedDevices]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !storeCenter) return;

    if (focusedDeviceId) {
      const focused = plottedDevices.find((entry) => entry.deviceId === focusedDeviceId);
      if (focused) {
        map.easeTo({
          center: [focused.coordinates.lng, focused.coordinates.lat],
          duration: 500,
        });
        return;
      }
    }

    map.easeTo({
      center: [storeCenter.lng, storeCenter.lat],
      zoom: 18,
      bearing: MAPBOX_DEFAULT_BEARING,
      duration: 500,
    });
  }, [focusedDeviceId, mapReady, plottedDevices, storeCenter]);

  useEffect(() => {
    if (!isMapActive || !mapRef.current) return undefined;
    const frameId = requestAnimationFrame(() => {
      mapRef.current?.resize();
    });
    const timeoutId = window.setTimeout(() => {
      mapRef.current?.resize();
    }, 150);
    const lateTimeoutId = window.setTimeout(() => {
      mapRef.current?.resize();
    }, 400);
    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(lateTimeoutId);
    };
  }, [isMapActive, plottedDevices.length, fillHeight]);

  const wrapperClass = fillHeight
    ? "relative flex flex-col flex-1 min-h-0 h-full bg-surface-container-low"
    : "flex flex-col min-h-0 bg-surface-container-high rounded-lg p-4 space-y-3";

  const mapClass = fillHeight
    ? `w-full flex-1 min-h-0 ${canRenderMap ? "block" : "hidden"}`
    : `w-full min-h-[280px] h-[32vh] rounded-lg overflow-hidden bg-surface-container-low ${
        canRenderMap ? "block" : "hidden"
      }`;

  return (
    <div className={wrapperClass}>
      {canRenderMap && (
        <div
          className={
            fillHeight
              ? "absolute top-3 left-3 z-10 flex flex-col gap-2 max-w-[min(100%,20rem)]"
              : "flex items-center gap-2"
          }
        >
          {showFloorPicker && (
            <select
              id="mdm-location-version-floor"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              className="text-xs rounded-lg border border-outline-variant/30 bg-surface-container-lowest/95 backdrop-blur-sm px-2 py-1.5 text-on-surface shadow-sm"
            >
              {floorOptions.map((floorOption) => (
                <option key={floorOption} value={floorOption}>
                  {floorOption}
                </option>
              ))}
              <option value={DEFAULT_FLOOR_ALL}>All floors</option>
            </select>
          )}
          {(mapStatusMessage ||
            (!plottedDevices.length && canRenderMap && !mapStatusMessage)) && (
            <p className="text-xs text-on-surface-variant bg-surface-container-lowest/95 backdrop-blur-sm rounded-lg px-2 py-1.5 shadow-sm">
              {mapStatusMessage ||
                (emptyCoordinatesMessage ||
                  (locationSource === "mdm"
                    ? "No MDM location coordinates for the selected devices."
                    : "No location log coordinates for the selected devices. Run Get Location/Version to fetch logs."))}
            </p>
          )}
        </div>
      )}

      {!canRenderMap && mapStatusMessage && (
        <p className={`text-xs text-on-surface-variant ${fillHeight ? "p-4" : ""}`}>
          {mapStatusMessage}
        </p>
      )}

      <div ref={mapContainerRef} className={mapClass} />
    </div>
  );
};

export default MdmLocationVersionMapPanel;
